/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, LabelProps, Legend, ReferenceLine, XAxis, YAxis,
} from 'recharts';
import { themeColors } from '../../Charts/common';
import { chartsConfig } from './config';
import { BaseLegend } from './Legend';
import { AmountLabel } from './AmountLabel';

interface BaseConfig {
  unit?: string;
  domain?: number[];
  type?: string;
}
interface BaseChartConfig {
  x?: BaseConfig,
  y?: BaseConfig;
}

interface BaseBarChartProps<T extends Record<string, unknown>> {
  overrideColorsByValueSign?: boolean;
  mainAxisDataKey: keyof T;
  data: T[];
  height?: number;
  width?: number;
  config?: BaseChartConfig;
  originReferenceLine?: boolean;
  yAxisDomain?: [number | string, number | string];
  xAxisDomain?: [number | string, number | string];
  showLabel?: boolean;
  showLegend?: boolean;
  legendLabels: Partial<Record<keyof T, string>>;
  dataKey: keyof T;
  margin?:{
    top: number;
    right: number;
    left: number;
    bottom: number;
  },
  layout: 'horizontal' | 'vertical';
}

// Sometimes, labels were clipped because of the position and size of the bars
// So we need to add a little offset to the labels based in height
function NameLabel(props: LabelProps & { viewBox?: { width: number } }) {
  const {
    y = 0, value, viewBox = { width: 0 },
  } = props;
  const isSmall = viewBox.width < 16;
  if (isSmall) {
    return (
      <text
        y={+y - 5}
        fill="#000"
        fontSize={12}
        textAnchor="start"
      >
        {value}
      </text>
    );
  }
  return (
    <text
      y={+y - 5}
      fill="black"
      fontSize={12}
      textAnchor="start"
    >
      {value}
    </text>
  );
}

function formatTickValue(value: number) {
  return Number.isInteger(value) ? value.toString() : value.toFixed(2);
}
export function BaseBarChart<T extends Record<string, unknown>>(
  props: BaseBarChartProps<T>
) {
  const {
    overrideColorsByValueSign = false,
    layout = 'horizontal',
    data,
    height = 250,
    width,
    mainAxisDataKey,
    legendLabels,
    dataKey,
    showLabel = false,
    showLegend = false,
    config,
    yAxisDomain,
    xAxisDomain,
    originReferenceLine,
    margin = {
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
    },
  } = props;

  const xAxisType = layout === 'horizontal' ? 'category' : 'number';
  const yAxisType = layout === 'horizontal' ? 'number' : 'category';
  const categoryAxisWidth = layout === 'vertical' ? 100 : undefined;
  const useReferenceLine = yAxisDomain?.some((value) => +value < 0) || originReferenceLine;
  return (
    <BarChart
      layout={layout}
      data={data}
      margin={margin}
      width={width}
      height={height}
      barCategoryGap={10}
    >
      <CartesianGrid strokeDasharray="3 3" vertical={layout === 'vertical'} horizontal={layout === 'horizontal'} />
      <XAxis
        unit={layout === 'horizontal' ? config?.x?.unit : config?.y?.unit}
        domain={layout === 'horizontal' ? xAxisDomain : yAxisDomain}
        type={xAxisType}
        tickFormatter={formatTickValue}
        tick={{ fontSize: chartsConfig.tick.fontSize }}
        dataKey={layout === 'horizontal' ? mainAxisDataKey as string : undefined}
        axisLine={layout === 'horizontal'}
      />
      <YAxis
        unit={layout === 'horizontal' ? config?.y?.unit : config?.x?.unit}
        domain={layout === 'vertical' ? yAxisDomain : xAxisDomain}
        type={yAxisType}
        tick={false}
        tickFormatter={formatTickValue}
        dataKey={layout === 'vertical' ? mainAxisDataKey as string : undefined}
        width={categoryAxisWidth}
        axisLine={!useReferenceLine && layout === 'vertical'}
        interval="preserveStartEnd"
      />
      {
        useReferenceLine && <ReferenceLine x={0} stroke="#666666" />
      }
      <Bar
        dataKey={dataKey as string}
        fill={themeColors.primary}
        isAnimationActive={false}
        maxBarSize={48}
      >
        {data.map((entry) => {
          const { name } = entry;
          const amount = Number(entry.amount);
          const isPositive = amount > 0;
          const color = isPositive ? '#BEDA94' : '#DE6E60';
          const fill = overrideColorsByValueSign ? color : themeColors.primary;
          return <Cell key={`Entry-cell-${name as string}-${amount}`} fill={fill} />;
        })}
        {showLabel && <LabelList fontSize={12} content={(args) => (<AmountLabel {...args} unit={layout === 'horizontal' ? config?.x?.unit : config?.y?.unit} />)} dataKey={dataKey as string} offset={5} fill="#000" />}
        {showLabel && <LabelList fontSize={12} content={<NameLabel />} dataKey="name" position={layout === 'horizontal' ? 'insideEnd' : 'top'} offset={4} fill="#000" />}
      </Bar>
      {showLegend && (
        <Legend
          className="w-100"
          content={(legProps) => (
            <BaseLegend
              payload={legProps?.payload ?? []}
              legendLabels={legendLabels}
            />
          )}
        />
      )}
    </BarChart>
  );
}
